import { createColumnHelper } from '@tanstack/react-table';
import classNames from 'classnames';
import {
  differenceInHours,
  differenceInMinutes,
  format,
  formatISO,
} from 'date-fns';
import { useTranslation } from 'next-i18next';
import { formatNumericValue, nameIdPairValidationSchema } from 'utils/general';
import { truncateString } from 'utils/general';
import * as yup from 'yup';

import { CreatePeriodicOperation } from '@/modules/periodicTasks/components/Create';
import CropsAndFields from '@/modules/periodicTasks/components/Create/Steps/CropsAndFields';
import Inputs from '@/modules/periodicTasks/components/Create/Steps/Inputs';
import OperationDetails from '@/modules/periodicTasks/components/Create/Steps/OperationDetails';
import OperationType from '@/modules/periodicTasks/components/Create/Steps/OperationType';
import Review from '@/modules/periodicTasks/components/Create/Steps/Review';
import WaterAmount from '@/modules/periodicTasks/components/Create/Steps/WaterAmount';
import { PeriodicOperationCategory } from '@/modules/periodicTasks/components/Details/logic';
import { IPeriodicTasks } from '@/modules/periodicTasks/types';
import { NextImage, Typography } from '@/shared/components/atoms';
import ValidateFieldModal from '@/shared/components/atoms/Demo/Modals/CreateFieldFirst';
import { IIconWithTitleDescriptionProps } from '@/shared/components/atoms/IconWithTItleDescription/IconWithTitleDescription';
import TippyTooltip from '@/shared/components/atoms/TippyTooltip';
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal';
import { IMultiStepFormStep } from '@/shared/components/molecules/MultiStepForm/IMultiStepForm';
import PartialListWithTooltip from '@/shared/components/molecules/PartialListWithTooltip';
import { roles } from '@/shared/constant/roles-and-permissions';
import { useAuth } from '@/shared/hooks/context/auth';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import useCheckPropertyFields from '@/shared/hooks/useCheckPropertyFields';
import { getLocalizedDate } from '@/shared/lib/dates';

import Arrow from '~/images/shared/ArrowUp.svg';
import CalendarIcon from '~/images/shared/calender.svg';
import ClipboardIcon from '~/images/shared/Clipboard.svg';
import DocumentIcon from '~/images/shared/document-text.svg';
import InfoCircle from '~/images/shared/ExleminationMark.svg';
import MessageIcon from '~/images/shared/MessageQuery.svg';
import PictureFrame from '~/images/shared/picture-frame.svg';
import ProfileIcon from '~/images/shared/Profile.svg';
import ScissorsIcon from '~/images/shared/Scissors.svg';
import Speedometer from '~/images/shared/Speedometer.svg';

export const useCreatePeriodicOperationUtils = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  //********** popup **********

  const { openModal } = useModal();
  const { user } = useAuth();

  const { hasFields } = useCheckPropertyFields();
  const isProductionOrFinanceManager =
    user?.userGroupId === roles.PRODUCTION_MANAGER ||
    user?.userGroupId === roles.FINANCIAL_MANAGER;

  const openCreatePeriodicOperationModal = () => {
    if (!hasFields || (isProductionOrFinanceManager && hasFields))
      openModal(<ValidateFieldModal />);
    else
      openModal(<CreatePeriodicOperation />, {
        variant: 'full',
        title: (
          <Typography variant='h2'>{t('create-periodic-operation')}</Typography>
        ),
        onClose: () => {
          openModal(
            <ConfirmationModal
              header={t('going-somewhere-else')}
              message={t('data-loss-warning')}
              variant='warning'
              dismissText={t('continue-editing')}
              confirmText={t('discard-and-exit')}
              onConfirmRemoveNumber={3}
              buttonsAlignment='col'
              preferredAction='dismiss'
            />
          );
        },
      });
  };
  // TODO: refactor this file
  // TODO: move opening tech and periodic operations in general logic file
  // TODO: move form steps functions into one file
  // TODO: keep only form validations here
  // TODO: move logic and mutation into separate file
  // TODO: update naming
  // TODO: need doc/comments for other engineers

  //********** Steps **********

  const waterAmountStep: IMultiStepFormStep<IPeriodicTasks.IOperationFormSchema> =
    {
      title: t('water-amount'),
      render: () => <WaterAmount />,
      fields: ['products'],
    };

  const inputsStep: IMultiStepFormStep<IPeriodicTasks.IOperationFormSchema> = {
    title: t('inputs-used'),
    render: () => <Inputs />,
    fields: ['products'],
  };

  const getFormSteps = (
    operationTypeId?: number
  ): IMultiStepFormStep<IPeriodicTasks.IOperationFormSchema>[] => [
    {
      title: t('task-category'),
      childrenTitle: t('choose-operation'),
      render: () => <OperationType />,
      fields: ['operationType', 'ApplicationMethod'],
    },
    {
      title: t('operation-details'),
      render: () => <OperationDetails />,
      fields: [
        'name',
        'assignee',
        'plannedDate',
        'notes',
        ...(operationTypeId === PeriodicOperationCategory.Spraying
          ? ['causes' as const]
          : []),
      ],
    },
    {
      title: t('crops-and-fields'),
      render: () => <CropsAndFields />,
      fields: ['crops', 'plots'],
    },
    ...(operationTypeId === undefined
      ? []
      : operationTypeId === PeriodicOperationCategory.Irrigation
      ? [waterAmountStep]
      : [inputsStep]),
    {
      title: t('review'),
      render: ({ setStep }) => <Review setStep={setStep} />,
      fields: [],
    },
  ];

  //********** Validation Schema **********
  // TODO: move this to general

  const plotValidationSchema: yup.SchemaOf<IPeriodicTasks.IOperationPlotFormValues> =
    yup.object().shape({
      plot: yup.object().shape({
        plotId: yup.number().nullable().required(t('this-field-is-required')),
        plotName: yup.string().nullable().required(),
        plotArea: yup.number().nullable().required(),
        areaUnit: yup.string().nullable().required(),
        cropId: yup.number().nullable().required(),
        cropImage: yup.string().nullable().required(),
        cropName: yup.string().nullable().required(),
      }),
      workingArea: yup
        .number()
        .nullable()
        .transform((value) => (isNaN(value) ? null : value))
        .moreThan(0, t('This field must be greater than zero.'))
        .test(
          'is-valid-working-area',
          t('working-area-cannot-exceed-plot-area'),
          function (value) {
            const { plotArea } = this.parent.plot;
            return plotArea == null || value == null || value <= plotArea;
          }
        )
        .required(t('this-field-is-required')),
      startTime: yup.date().required(t('this-field-is-required')),
      endTime: yup
        .date()
        .required(t('this-field-is-required'))
        .when('startTime', (startTime, schema) =>
          startTime
            ? schema
                .min(startTime, t('end-time-must-be-after-start-time'))
                .test(
                  'time-difference',
                  t('time-diff-min-one-minute'),
                  function (endTime: Date) {
                    return differenceInMinutes(endTime, startTime) >= 1;
                  }
                )
            : schema
        ),
    });

  const productObjectValidationSchema: yup.SchemaOf<IPeriodicTasks.IOperationProductObject> =
    yup
      .object()
      .shape({
        product_id: yup.number().required(t('this-field-is-required')),
        product_name: yup.string().notRequired(),
        measurement_type: yup.string().notRequired(),
        active_substances: yup
          .array()
          .of(
            yup.object().shape({
              id: yup.number().notRequired(),
              concentration: yup.number().required(),
              activeSubstanceName: yup.string().notRequired(),
              name: yup.string().notRequired(),
            })
          )
          .nullable()
          .notRequired(),
      })
      .required();

  const productValidationSchema: yup.SchemaOf<IPeriodicTasks.IOperationProductFormValues> =
    yup.object().shape({
      product: productObjectValidationSchema.required(),
      unit: yup.string().nullable().required(t('this-field-is-required')),
      rate: yup
        .number()
        .nullable()
        .transform((value) => (isNaN(value) ? null : value))
        .moreThan(0, t('This field must be greater than zero.'))
        .required(t('this-field-is-required')),
      amount: yup
        .number()
        .nullable()
        .transform((value) => (isNaN(value) ? null : value))
        .moreThan(0, t('This field must be greater than zero.'))
        .required(t('this-field-is-required')),
    });

  const operationFormValidationSchema: yup.SchemaOf<IPeriodicTasks.IOperationFormSchema> =
    yup.object().shape({
      operationType: nameIdPairValidationSchema(t).shape({
        icon: yup.string().required(),
      }),
      ApplicationMethod: nameIdPairValidationSchema(t),
      assignee: nameIdPairValidationSchema(t),
      causes: yup
        .array()
        .of(nameIdPairValidationSchema(t).required())
        .when('operationType.id', {
          is: PeriodicOperationCategory.Spraying,
          then: (schema) =>
            schema
              .required(t('this-field-is-required'))
              .min(1, t('this-field-is-required')),
          otherwise: (schema) => schema.notRequired(),
        }),
      name: yup
        .string()
        .required(t('this-field-is-required'))
        .max(
          60,
          t('this-field-not-accept-more-than-x-character', { count: 60 })
        ),
      creationDate: yup.string().required(t('this-field-is-required')),
      plannedDate: yup.date().required(t('this-field-is-required')),
      status: yup.string().required(t('this-field-is-required')),
      crops: yup
        .array()
        .of(yup.number().required(t('this-field-is-required')))
        .min(1, t('this-field-is-required'))
        .required(t('this-field-is-required')),
      plots: yup
        .array()
        .of(plotValidationSchema)
        .required(t('this-field-is-required'))
        .min(1, t('this-field-is-required')),
      products: yup
        .array()
        .of(productValidationSchema)
        .required(t('this-field-is-required'))
        .min(1, t('this-field-is-required')),
      notes: yup
        .string()
        .notRequired()
        .max(
          500,
          t('this-field-not-accept-more-than-x-character', { count: 500 })
        ),
    });

  //********** step 2 **********

  const getCategoryJobTypes = (
    categoryId: number,
    items: IPeriodicTasks.IJobCategoriesItemsSchema[]
  ) => {
    return items.find((item) => item.id === categoryId)?.jobTypes;
  };

  //********** step 4 **********

  const getPlotsTotalWorkingArea = (
    plots: IPeriodicTasks.IOperationPlotFormValues[]
  ) => {
    return plots.reduce(
      (accumulator, currentValue) =>
        accumulator + Number(currentValue.workingArea),
      0
    );
  };

  const getAmountFromRate = (value: string, totalWorkArea: number) => {
    const rate = Number(value);
    return value ? totalWorkArea * rate : null;
  };

  const getRateFromAmount = (value: string, totalWorkArea: number) => {
    const amountValue = Number(value);
    return value ? amountValue / totalWorkArea : null;
  };

  //********** Review step **********

  const sharedCardProps = {
    titleTypography: {
      variant: 'caption' as const,
    },
    descriptionTypography: {
      weight: 'medium' as const,
    },
    iconGap: 'gap-16',
  };

  const getGeneralcardItems = (
    formValues: IPeriodicTasks.IOperationFormSchema
  ): IIconWithTitleDescriptionProps[] => [
    {
      title: t('operation-type'),
      description: formValues.operationType.name,
      icon: (
        <NextImage
          alt='operationIcon'
          src={formValues.operationType.icon}
          width={24}
          height={24}
        />
      ),
    },
    {
      title: t('application-method'),
      description: formValues.ApplicationMethod.name,
      icon: <ScissorsIcon width='24' height='24' />,
    },
    {
      title: t('assignee'),
      description: formValues.assignee.name,
      icon: <ProfileIcon width='24' height='24' />,
    },
    {
      title: t('planned-date'),
      description: format(formValues.plannedDate, "dd/MM/yyyy'"),
      icon: <CalendarIcon width='24' height='24' />,
    },
    {
      title: t('descriptive-name'),
      description: (
        <div title={formValues.name}>
          {truncateString({ text: formValues.name, length: 30 })}
        </div>
      ),
      icon: <DocumentIcon width='24' height='24' />,
    },
    ...(formValues.operationType.id === PeriodicOperationCategory.Spraying
      ? [
          {
            title: t('cause'),
            description: (
              <PartialListWithTooltip
                limit={2}
                items={formValues.causes.map((cause) => ({
                  description: cause.name,
                }))}
              />
            ),
            icon: <MessageIcon width='24' height='24' />,
          },
        ]
      : []),
    {
      title: t('notes'),
      description: formValues?.notes ? (
        <div title={formValues.notes}>
          {truncateString({ text: formValues.notes, length: 100 })}
        </div>
      ) : (
        '-'
      ),
      icon: <ClipboardIcon width='24' height='24' />,
    },
  ];

  const getWaterAmountCardItems = (
    formValues: IPeriodicTasks.IOperationFormSchema,
    areaUnit?: string | null
  ): IIconWithTitleDescriptionProps[] => [
    {
      title: t('rate'),
      description: `${formatNumericValue(
        Number(formValues.products[0].rate)
      )} ${t('m3')} ${t('per-unit', {
        unit: t(areaUnit as string),
      })}`,
      icon: <Speedometer width='24' height='24' />,
    },
    {
      title: t('total-amount'),
      description: `${formatNumericValue(
        formValues.products[0].amount
          ? Number(formValues.products[0].amount)
          : 0
      )} ${t('m3')}`,
      icon: <PictureFrame width='24' height='24' />,
    },
  ];

  const fieldsColumnHelper =
    createColumnHelper<IPeriodicTasks.IOperationPlotFormValues>();
  const productsColumnHelper =
    createColumnHelper<IPeriodicTasks.IOperationProductsTableColumn>();

  const getFieldsTableColumns = (unit?: string | null) => [
    fieldsColumnHelper.accessor('plot', {
      header: t('Field'),
      id: 'plot',
      cell: ({ getValue }) => (
        <PartialListWithTooltip
          limit={1}
          items={[
            {
              description: getValue().plotName,
              imageURL: getValue().cropImage,
            },
          ]}
        />
      ),
      footer: t('total'),
    }),
    fieldsColumnHelper.accessor('plot', {
      header: t('total-area'),
      id: 'totalArea',
      cell: ({ getValue }) => (
        <Typography>{`${formatNumericValue(Number(getValue().plotArea))} ${t(
          getValue().areaUnit as string
        )}`}</Typography>
      ),
      footer: ({ table }) =>
        `${table
          .getFilteredRowModel()
          .rows.reduce(
            (total, row) => total + Number(row.original.plot.plotArea),
            0
          )} ${t(unit as string)}`,
    }),
    fieldsColumnHelper.accessor('workingArea', {
      header: t('work-area'),
      id: 'workArea',
      cell: ({ getValue, row: { original } }) => (
        <Typography>{`${formatNumericValue(Number(getValue()))} ${t(
          original.plot.areaUnit as string
        )}`}</Typography>
      ),
      footer: ({ table }) =>
        `${table
          .getFilteredRowModel()
          .rows.reduce(
            (total, row) => total + Number(row.original.workingArea),
            0
          )} ${t(unit as string)}`,
    }),
    fieldsColumnHelper.accessor('startTime', {
      header: t('applying-time'),
      id: 'applyingTime',
      cell: ({ getValue, row: { original } }) => (
        <Typography>
          {t('from-to-hours', {
            startTime: getLocalizedDate({
              date: getValue(),
              locale: language,
              formatDate: 'h:mm a',
            }),
            endTime: getLocalizedDate({
              date: original.endTime,
              locale: language,
              formatDate: 'h:mm a',
            }),
          })}
        </Typography>
      ),
    }),
    fieldsColumnHelper.accessor('endTime', {
      header: t('total-hours'),
      id: 'endTime',
      cell: ({
        row: {
          original: { endTime, startTime },
        },
      }) => {
        const diffHours = differenceInHours(
          new Date(endTime),
          new Date(startTime)
        );
        const diffMins =
          differenceInMinutes(new Date(endTime), new Date(startTime)) % 60;
        return (
          <Typography className='text-12 text-text-default'>
            {diffHours}.{Math.round((diffMins / 60) * 100)}
          </Typography>
        );
      },
      footer: ({ table }) => {
        const totalMinutes = table
          .getFilteredRowModel()
          .rows.reduce(
            (total, row) =>
              total +
              differenceInMinutes(
                new Date(row.original.endTime),
                new Date(row.original.startTime)
              ),
            0
          );
        const totalHours = `${Math.floor(totalMinutes / 60)}.${Math.round(
          ((totalMinutes % 60) / 60) * 100
        )}`;
        return `${totalHours} ${t('hours')}`;
      },
    }),
  ];

  const getProductTableColumns = (
    unit: string | null | undefined,
    operationTypeId: number
  ) => [
    productsColumnHelper.accessor('product', {
      header: t('input'),
      cell: ({ getValue, row: { depth, original } }) =>
        depth > 0 ? (
          <PartialListWithTooltip
            items={[
              {
                description: original?.plot?.plot.plotName,
                imageURL: original?.plot?.plot.cropImage,
              },
            ]}
          />
        ) : (
          <div className='flex items-center gap-8'>
            <Typography>{getValue()?.product_name}</Typography>
            {operationTypeId !== PeriodicOperationCategory.Planting &&
              Boolean(getValue()?.active_substances?.length) && (
                <TippyTooltip
                  content={
                    <div className='flex w-fit min-w-[100px] flex-col'>
                      {getValue()?.active_substances?.map((substance) => (
                        <Typography key={substance.id}>
                          {`${substance.activeSubstanceName} ${substance.concentration}%`}
                        </Typography>
                      ))}
                    </div>
                  }
                >
                  <span className='cursor-pointer text-primary-default'>
                    <InfoCircle width='16' height='16' />
                  </span>
                </TippyTooltip>
              )}
          </div>
        ),
    }),
    productsColumnHelper.accessor('rate', {
      header: t('rate-per-unit', { unit: t(unit as string) }),
      cell: ({ getValue, row: { original } }) => (
        <Typography weight='medium'>
          {`${formatNumericValue(Number(getValue()))} ${t(
            original.unit as string
          )}/${t(unit as string)}`}
        </Typography>
      ),
    }),
    productsColumnHelper.accessor('amount', {
      header: t('total-amount'),
      cell: ({ getValue, row: { original } }) => (
        <Typography weight='medium'>
          {`${formatNumericValue(Number(getValue()))} ${t(
            original.unit as string
          )}`}
        </Typography>
      ),
    }),
    productsColumnHelper.display({
      id: 'expand',
      cell: ({ row }) =>
        row.getCanExpand() && (
          <div
            className='flex cursor-pointer items-center justify-end'
            onClick={row.getToggleExpandedHandler()}
          >
            <span
              className={classNames(
                !row.getIsExpanded() && ' rotate-180',
                'transition-all duration-300'
              )}
            >
              <Arrow />
            </span>
          </div>
        ),
    }),
  ];

  const getProductsTableData = (
    products: IPeriodicTasks.IOperationProductFormValues[],
    plots: IPeriodicTasks.IOperationPlotFormValues[]
  ) => {
    return products.map((product) => ({
      ...product,
      subRows: plots.map((plot) => ({
        ...product,
        plot,
        amount: Number(product.rate) * (plot.workingArea as number),
      })),
    }));
  };

  //********** Mutation **********/

  const getMutationObject = (
    formValues: IPeriodicTasks.IOperationFormSchema
  ): IPeriodicTasks.ICreateOperationSchema => {
    const isIrrigation =
      formValues.operationType.id === PeriodicOperationCategory.Irrigation;
    return {
      jobTypeId: formValues.ApplicationMethod.id,
      name: formValues.name,
      plannedDate: formatISO(formValues.plannedDate, {
        representation: 'date',
      }),
      creationDate: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
      notes: formValues.notes,
      status: formValues.status,
      causes: formValues?.causes?.length
        ? formValues.causes.map((cause) => cause.id)
        : undefined,
      assigneeId: formValues.assignee.id,
      products: formValues.products.map((product) => ({
        productId: isIrrigation ? 0 : (product.product?.product_id as number),
        unit: product.unit as string,
        rate: product.rate as number,
        amount: product.amount as number,
        plots: formValues.plots.map((item) => ({
          plotId: item.plot.plotId as number,
          cropId: item.plot.cropId as number,
          workingArea: item.workingArea as number,
          startTime: item.startTime,
          endTime: item.endTime,
        })),
      })),
    };
  };

  return {
    openCreatePeriodicOperationModal,
    getFormSteps,
    operationFormValidationSchema,
    getCategoryJobTypes,
    getFieldsTableColumns,
    getProductTableColumns,
    getPlotsTotalWorkingArea,
    getAmountFromRate,
    getRateFromAmount,
    getProductsTableData,
    getGeneralcardItems,
    getWaterAmountCardItems,
    sharedCardProps,
    getMutationObject,
  };
};
